import { inject, Injectable, signal } from '@angular/core';
import { KeycloakClient } from '@core/services/keycloak/keycloak.client';
import { TranslateService } from '@ngx-translate/core';
import { UserManagementHttpClient } from 'user-management/services/http/user-management-http-client';

/**
 * Names the planner behind a reconciliation event. The tenant's user list
 * is loaded once, on first use; until it has landed, or for a user it does
 * not contain, the label falls back to "another planner".
 */
@Injectable({ providedIn: 'root' })
export class ConsensusPlannerNamesService {
  private readonly users = inject(UserManagementHttpClient);
  private readonly keycloak = inject(KeycloakClient);
  private readonly translate = inject(TranslateService);

  private readonly labelsById = signal<ReadonlyMap<string, string> | null>(
    null,
  );
  private loading = false;

  get currentUserId(): string | null {
    return this.keycloak.getUserId();
  }

  isCurrentUser(userId: string | undefined): boolean {
    const me = this.currentUserId;
    return !!me && userId === me;
  }

  labelFor(userId: string | undefined): string {
    const label = userId ? this.labelsById()?.get(userId) : undefined;
    if (label) return label;
    this.ensureLoaded();
    return this.translate.instant('CONSENSUS.ANOTHER_PLANNER');
  }

  private ensureLoaded(): void {
    if (this.loading || this.labelsById()) return;
    this.loading = true;
    this.users.getAllCachedUsers().subscribe({
      next: (users) => {
        this.labelsById.set(
          new Map(
            users.map((user) => [
              user.id,
              [user.firstName, user.lastName].filter(Boolean).join(' ') ||
                user.email,
            ]),
          ),
        );
      },
      error: () => {
        this.loading = false;
      },
    });
  }
}
